/**
 * Playtest Arena ESM→deps compiler.
 * Turns fetched game file strings into a module graph PhaserGameMount can eval
 * via new Function (not real browser ESM). Keep fetch prefixes in sync with
 * functions/game/fetchPlaytestArenaFiles.js PLAYTEST_DIR_PREFIXES.
 */

/**
 * @param {unknown} value
 * @returns {string}
 */
export function asTrimmedString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * @param {string} fromDir
 * @param {string} spec
 * @returns {string}
 */
export function resolveRelativePath(fromDir, spec) {
  const base = asTrimmedString(fromDir).replace(/\\/g, '/').replace(/\/+$/, '');
  const parts = base ? base.split('/').filter(Boolean) : [];
  const specParts = asTrimmedString(spec).replace(/\\/g, '/').split('/');
  for (const part of specParts) {
    if (!part || part === '.') continue;
    if (part === '..') {
      parts.pop();
      continue;
    }
    parts.push(part);
  }
  return parts.join('/');
}

/**
 * @param {string} filePath
 * @returns {string[]}
 */
export function moduleKeysForPath(filePath) {
  const normalized = asTrimmedString(filePath).replace(/\\/g, '/');
  if (!normalized) return [];
  const keys = new Set([normalized]);
  const noExt = normalized.replace(/\.(jsx?|mjs|cjs|tsx?)$/i, '');
  keys.add(noExt);
  const parts = noExt.split('/').filter(Boolean);
  if (parts.length) {
    keys.add(parts[parts.length - 1]);
    if (parts.length >= 2) {
      keys.add(`${parts[parts.length - 2]}/${parts[parts.length - 1]}`);
    }
    if (parts.length >= 3) {
      keys.add(
        `${parts[parts.length - 3]}/${parts[parts.length - 2]}/${parts[parts.length - 1]}`,
      );
    }
  }
  return [...keys];
}

/**
 * Keys for an import specifier, including resolution against the importer.
 * @param {string} spec
 * @param {string} [importerPath]
 * @returns {string[]}
 */
export function moduleKeysForSpec(spec, importerPath = '') {
  const keys = new Set(moduleKeysForPath(spec));
  const normalizedSpec = asTrimmedString(spec).replace(/\\/g, '/');
  const importer = asTrimmedString(importerPath).replace(/\\/g, '/');
  if (
    importer &&
    (normalizedSpec.startsWith('./') || normalizedSpec.startsWith('../'))
  ) {
    const importerDir = importer.replace(/\/[^/]+$/, '');
    const resolved = resolveRelativePath(importerDir, normalizedSpec);
    moduleKeysForPath(resolved).forEach((key) => keys.add(key));
  }
  return [...keys];
}

/**
 * @param {string} spec
 * @param {string} [importerPath]
 * @returns {string}
 */
export function depsLookupExpr(spec, importerPath = '') {
  const keys = moduleKeysForSpec(spec, importerPath);
  if (!keys.length) return 'undefined';
  return keys.map((key) => `deps[${JSON.stringify(key)}]`).join(' || ');
}

/**
 * @param {string} spec
 * @returns {boolean}
 */
function isPhaserSpec(spec) {
  return String(spec).replace(/\\/g, '/').toLowerCase().includes('phaser');
}

/**
 * @param {string} lookupExpr
 * @returns {string}
 */
function unwrapDefaultExpr(lookupExpr) {
  return (
    `(() => { const __m = (${lookupExpr}); ` +
    `return __m && typeof __m === "object" && "default" in __m ` +
    `? __m.default : __m; })()`
  );
}

/**
 * @param {string} inner
 * @returns {string} e.g. "{ a, b: c }"
 */
function namedBindingsToDestructure(inner) {
  const parts = String(inner || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const fields = parts.map((part) => {
    const asMatch = part.match(/^(\w+)\s+as\s+(\w+)$/);
    if (asMatch) {
      return `${asMatch[1]}: ${asMatch[2]}`;
    }
    return part;
  });
  return `{ ${fields.join(', ')} }`;
}

/**
 * Remove Phaser ESM imports — Phaser is injected into the Function scope.
 * @param {string} rawCode
 * @returns {string}
 */
export function stripPhaserImports(rawCode) {
  if (typeof rawCode !== 'string' || !rawCode) return '';
  return rawCode
    .replace(
      /import\s+(?:\*\s+as\s+)?Phaser\s+from\s+['"]phaser['"];?/g,
      '',
    )
    .replace(
      /^\s*import\s+Phaser\s*,\s*\{[^}]*\}\s*from\s+['"]phaser['"]\s*;?\s*$/gm,
      '',
    )
    .replace(
      /^\s*import\s+\{[^}]*\}\s*from\s+['"]phaser['"]\s*;?\s*$/gm,
      '',
    );
}

/**
 * Rewrite static ES module imports to read from the compile-time deps registry.
 * @param {string} source
 * @param {string} [importerPath]
 * @returns {string}
 */
export function rewriteImportsToDeps(source, importerPath = '') {
  if (typeof source !== 'string' || !source) return '';
  let code = source;

  // import Default, * as ns from 'spec'
  code = code.replace(
    /^\s*import\s+(\w+)\s*,\s*\*\s+as\s+(\w+)\s+from\s+['"]([^'"]+)['"]\s*;?\s*$/gm,
    (_full, defBinding, nsBinding, spec) => {
      if (isPhaserSpec(spec)) return '';
      const lookup = depsLookupExpr(spec, importerPath);
      return (
        `const ${nsBinding} = ${lookup} || {};\n` +
        `const ${defBinding} = ${unwrapDefaultExpr(lookup)};`
      );
    },
  );

  // import Default, { a, b as c } from 'spec'
  code = code.replace(
    /^\s*import\s+(\w+)\s*,\s*\{([^}]+)\}\s*from\s+['"]([^'"]+)['"]\s*;?\s*$/gm,
    (_full, defBinding, namedInner, spec) => {
      if (defBinding === 'Phaser' && isPhaserSpec(spec)) return '';
      if (isPhaserSpec(spec)) return '';
      const lookup = depsLookupExpr(spec, importerPath);
      const destructure = namedBindingsToDestructure(namedInner);
      return (
        `const ${defBinding} = ${unwrapDefaultExpr(lookup)};\n` +
        `const ${destructure} = ${lookup} || {};`
      );
    },
  );

  // import * as Name from 'spec'
  code = code.replace(
    /^\s*import\s+\*\s+as\s+(\w+)\s+from\s+['"]([^'"]+)['"]\s*;?\s*$/gm,
    (_full, nsBinding, spec) => {
      if (nsBinding === 'Phaser' && isPhaserSpec(spec)) return '';
      if (isPhaserSpec(spec)) return '';
      return `const ${nsBinding} = ${depsLookupExpr(spec, importerPath)} || {};`;
    },
  );

  // import { a, b as c } from 'spec'
  code = code.replace(
    /^\s*import\s+\{([^}]+)\}\s*from\s+['"]([^'"]+)['"]\s*;?\s*$/gm,
    (_full, namedInner, spec) => {
      if (isPhaserSpec(spec)) return '';
      const destructure = namedBindingsToDestructure(namedInner);
      return (
        `const ${destructure} = ${depsLookupExpr(spec, importerPath)} || {};`
      );
    },
  );

  // import Default from 'spec'
  code = code.replace(
    /^\s*import\s+(\w+)\s+from\s+['"]([^'"]+)['"]\s*;?\s*$/gm,
    (_full, binding, spec) => {
      if (binding === 'Phaser' && isPhaserSpec(spec)) return '';
      if (isPhaserSpec(spec)) return '';
      const lookup = depsLookupExpr(spec, importerPath);
      return `const ${binding} = ${unwrapDefaultExpr(lookup)};`;
    },
  );

  return code;
}

/**
 * Rewrite dynamic import() to a synchronous deps lookup (Arena has no loader).
 * Handles both `await import('x')` and bare `import('x')`.
 * @param {string} source
 * @param {string} [importerPath]
 * @returns {string}
 */
export function rewriteDynamicImports(source, importerPath = '') {
  if (typeof source !== 'string' || !source) return '';
  let code = source;
  // Prefer awaiting form first so we drop the await keyword.
  code = code.replace(
    /\bawait\s+import\s*\(\s*(['"])([^'"]+)\1\s*\)/g,
    (_full, _q, spec) => `(${depsLookupExpr(spec, importerPath)})`,
  );
  // Bare import('x') without await (avoid lookbehind for older browsers).
  code = code.replace(
    /(^|[^.\w$])import\s*\(\s*(['"])([^'"]+)\2\s*\)/g,
    (full, prefix, _q, spec) =>
      `${prefix}(${depsLookupExpr(spec, importerPath)})`,
  );
  return code;
}

/**
 * Strip leftover import lines and normalize exports into a module namespace.
 * @param {string} source
 * @returns {string}
 */
export function prepareModuleBody(source) {
  let code = String(source || '')
    .replace(/^\s*import\s+[\s\S]*?from\s+['"][^'"]+['"]\s*;?\s*$/gm, '')
    .replace(/^\s*import\s+['"][^'"]+['"]\s*;?\s*$/gm, '');

  /** @type {string[]} */
  const exportAssigns = [];

  code = code.replace(/export\s+default\s+class\s+(\w+)/g, (_m, name) => {
    exportAssigns.push(`__exports.default = ${name};`);
    return `class ${name}`;
  });

  code = code.replace(/export\s+class\s+(\w+)/g, (_m, name) => {
    exportAssigns.push(`__exports[${JSON.stringify(name)}] = ${name};`);
    return `class ${name}`;
  });

  code = code.replace(/export\s+async\s+function\s+(\w+)/g, (_m, name) => {
    exportAssigns.push(`__exports[${JSON.stringify(name)}] = ${name};`);
    return `async function ${name}`;
  });

  code = code.replace(/export\s+function\s+(\w+)/g, (_m, name) => {
    exportAssigns.push(`__exports[${JSON.stringify(name)}] = ${name};`);
    return `function ${name}`;
  });

  code = code.replace(
    /export\s+(const|let|var)\s+(\w+)\s*=/g,
    (_m, kind, name) => {
      exportAssigns.push(`__exports[${JSON.stringify(name)}] = ${name};`);
      return `${kind} ${name} =`;
    },
  );

  code = code.replace(/export\s*\{([^}]+)\}\s*;?/g, (_m, inner) => {
    const parts = String(inner)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return parts
      .map((part) => {
        const asMatch = part.match(/^(\w+)\s+as\s+(\w+)$/);
        if (asMatch) {
          return (
            `__exports[${JSON.stringify(asMatch[2])}] = ${asMatch[1]};`
          );
        }
        return `__exports[${JSON.stringify(part)}] = ${part};`;
      })
      .join('\n');
  });

  // Remaining `export default <expr>`
  code = code.replace(/export\s+default\s+/g, '__exports.default = ');

  const hasDefaultAssign =
    /__exports\.default\s*=/.test(code) ||
    exportAssigns.some((line) => /__exports\.default\s*=/.test(line));

  if (!hasDefaultAssign) {
    const namedScene = code.match(/class\s+(\w+)\s+extends\s+Phaser\.Scene/);
    if (namedScene) {
      exportAssigns.push(`__exports.default = ${namedScene[1]};`);
    }
  }

  const footer = exportAssigns.join('\n');
  return (
    `const __exports = { __esModule: true };\n${code}\n${footer}\n` +
    `return __exports;`
  );
}

/**
 * @param {string} fileContent
 * @param {string} filePath
 * @param {Record<string, unknown>} deps
 * @param {typeof import('phaser')} Phaser
 * @returns {Record<string, unknown>}
 */
export function compileApprovedFile(fileContent, filePath, deps, Phaser) {
  const raw = asTrimmedString(fileContent);
  if (!raw) {
    throw new Error(`Empty file content for ${filePath || '(unknown path)'}`);
  }

  const processedCode = stripPhaserImports(raw);
  const withImports = rewriteImportsToDeps(processedCode, filePath);
  const withDynamic = rewriteDynamicImports(withImports, filePath);
  const body = prepareModuleBody(withDynamic);
  // eslint-disable-next-line no-new-func
  const factory = new Function(
    'Phaser',
    'deps',
    `"use strict";\n${body}`,
  );
  const result = factory(Phaser, deps);
  if (result && typeof result === 'object') {
    return /** @type {Record<string, unknown>} */ (result);
  }
  return { __esModule: true, default: result };
}

/**
 * Extract static `from '…'` / `from "…"` specs (not dynamic import()).
 * @param {string} source
 * @returns {string[]}
 */
export function extractStaticImportSpecs(source) {
  if (typeof source !== 'string' || !source) return [];
  const specs = [];
  const re = /\bfrom\s+['"]([^'"]+)['"]/g;
  let match = re.exec(source);
  while (match) {
    const spec = match[1];
    if (spec && !isPhaserSpec(spec)) specs.push(spec);
    match = re.exec(source);
  }
  return specs;
}

/**
 * @param {object} row
 * @returns {string}
 */
function rowContent(row) {
  if (!row || typeof row !== 'object') return '';
  return (
    asTrimmedString(row.content) ||
    asTrimmedString(row.patch_content) ||
    asTrimmedString(row.source)
  );
}

/**
 * Topological order using static import edges. Dynamic import() is ignored
 * (runtime-only, e.g. SpinService → MockSpinService).
 * @param {Array<object>} files already coarse-sorted
 * @returns {typeof files}
 */
export function topoSortByStaticImports(files) {
  const rows = Array.isArray(files) ? [...files] : [];
  if (rows.length <= 1) return rows;

  /** @type {Map<string, object>} */
  const byPath = new Map();
  /** @type {Map<string, Set<string>>} */
  const keyToPaths = new Map();

  rows.forEach((row) => {
    const path = approvedFilePath(row);
    if (!path) return;
    byPath.set(path, row);
    moduleKeysForPath(path).forEach((key) => {
      if (!keyToPaths.has(key)) keyToPaths.set(key, new Set());
      keyToPaths.get(key).add(path);
    });
  });

  /**
   * @param {string} spec
   * @param {string} importerPath
   * @returns {string|null}
   */
  const resolveDepPath = (spec, importerPath) => {
    const keys = moduleKeysForSpec(spec, importerPath);
    for (const key of keys) {
      const hits = keyToPaths.get(key);
      if (!hits || !hits.size) continue;
      const list = [...hits].filter((p) => p !== importerPath);
      if (!list.length) continue;
      list.sort((a, b) => b.length - a.length);
      return list[0];
    }
    return null;
  };

  /** path -> set of paths it statically depends on (must compile first) */
  /** @type {Map<string, Set<string>>} */
  const depsOf = new Map();
  byPath.forEach((_row, path) => {
    depsOf.set(path, new Set());
  });

  byPath.forEach((row, path) => {
    extractStaticImportSpecs(rowContent(row)).forEach((spec) => {
      const depPath = resolveDepPath(spec, path);
      if (!depPath || depPath === path) return;
      depsOf.get(path).add(depPath);
    });
  });

  /** @type {Map<string, number>} */
  const indegree = new Map();
  byPath.forEach((_row, path) => {
    indegree.set(path, depsOf.get(path).size);
  });

  /** @type {string[]} */
  const queue = [];
  rows.forEach((row) => {
    const path = approvedFilePath(row);
    if (path && indegree.get(path) === 0) queue.push(path);
  });

  /** @type {string[]} */
  const orderedPaths = [];
  const visited = new Set();

  while (queue.length) {
    const path = queue.shift();
    if (!path || visited.has(path)) continue;
    visited.add(path);
    orderedPaths.push(path);

    byPath.forEach((_row, other) => {
      const set = depsOf.get(other);
      if (!set || !set.has(path)) return;
      set.delete(path);
      const next = (indegree.get(other) || 0) - 1;
      indegree.set(other, next);
      if (next === 0) queue.push(other);
    });
  }

  rows.forEach((row) => {
    const path = approvedFilePath(row);
    if (path && !visited.has(path)) orderedPaths.push(path);
  });

  return orderedPaths.map((path) => byPath.get(path)).filter(Boolean);
}

/**
 * utils/services before entities/scenes; config last (imports scenes).
 * Within a rank, shallower paths first so SpinService compiles before
 * services/spin/MockSpinService.js. Then topo-sort by static imports so
 * wallet fixtures precede MockSpinService even at the same depth.
 * @param {Array<object>} files
 * @returns {typeof files}
 */
export function sortApprovedFiles(files) {
  const rank = (path) => {
    const p = asTrimmedString(path).replace(/\\/g, '/').toLowerCase();
    if (p.includes('/utils/') || p.startsWith('utils/') || p.includes('/src/utils/')) {
      return 0;
    }
    if (
      p.includes('/services/') ||
      p.startsWith('services/') ||
      p.includes('/src/services/')
    ) {
      return 1;
    }
    if (p.includes('/entities/') || p.startsWith('entities/')) return 2;
    if (p.endsWith('player.js')) return 2;
    if (p.includes('/scenes/') || p.startsWith('scenes/')) return 3;
    if (p.includes('/config/') || p.startsWith('config/')) return 4;
    return 5;
  };

  const depth = (path) =>
    asTrimmedString(path).replace(/\\/g, '/').split('/').filter(Boolean)
      .length;

  const coarse = [...files].sort((a, b) => {
    const pathA = a.path || a.target_file_path || a.file_path || '';
    const pathB = b.path || b.target_file_path || b.file_path || '';
    const rankDiff = rank(pathA) - rank(pathB);
    if (rankDiff !== 0) return rankDiff;
    const depthDiff = depth(pathA) - depth(pathB);
    if (depthDiff !== 0) return depthDiff;
    return String(pathA).localeCompare(String(pathB));
  });

  return topoSortByStaticImports(coarse);
}

/**
 * @param {object} row
 * @returns {string}
 */
export function approvedFilePath(row) {
  if (!row || typeof row !== 'object') return '';
  return (
    asTrimmedString(row.path) ||
    asTrimmedString(row.target_file_path) ||
    asTrimmedString(row.file_path) ||
    asTrimmedString(row.id)
  );
}

/**
 * @param {Array<object>} files
 * @returns {Array<object>}
 */
export function dedupeApprovedFilesByPath(files) {
  const rows = Array.isArray(files) ? files : [];
  const byPath = new Map();
  let anonymous = 0;
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const normalized = approvedFilePath(row).replace(/\\/g, '/').toLowerCase();
    const key = normalized || `__anonymous_${anonymous++}`;
    byPath.set(key, row);
  }
  return [...byPath.values()];
}

/**
 * @param {unknown} moduleOrValue
 * @returns {unknown}
 */
export function unwrapModuleDefault(moduleOrValue) {
  if (
    moduleOrValue &&
    typeof moduleOrValue === 'object' &&
    'default' in /** @type {object} */ (moduleOrValue)
  ) {
    return /** @type {{ default: unknown }} */ (moduleOrValue).default;
  }
  return moduleOrValue;
}

/**
 * @param {Function} SceneClass
 * @returns {string}
 */
export function resolveSceneKey(SceneClass) {
  if (typeof SceneClass !== 'function') return '';
  try {
    const probe = new SceneClass();
    const fromSys =
      probe &&
      probe.sys &&
      probe.sys.settings &&
      typeof probe.sys.settings.key === 'string'
        ? probe.sys.settings.key.trim()
        : '';
    if (fromSys) return fromSys;
  } catch (err) {
    void err;
  }
  const name =
    typeof SceneClass.name === 'string' ? SceneClass.name.trim() : '';
  if (name && name !== 'Scene' && name !== 'Function') return name;
  return '';
}

/**
 * @param {unknown} value
 * @param {typeof import('phaser')} Phaser
 * @returns {boolean}
 */
export function isPhaserSceneClass(value, Phaser) {
  if (typeof value !== 'function' || !Phaser || !Phaser.Scene) return false;
  try {
    return value.prototype instanceof Phaser.Scene;
  } catch (err) {
    void err;
    return false;
  }
}

/**
 * Compile approved repository file strings into Phaser.Scene classes.
 * @param {Array<object> | null | undefined} approvedFiles
 * @param {typeof import('phaser')} Phaser
 * @returns {{ scenes: Function[], errors: string[], registry: Record<string, unknown> }}
 */
export function compileApprovedGameFiles(approvedFiles, Phaser) {
  const rows = sortApprovedFiles(
    dedupeApprovedFilesByPath(Array.isArray(approvedFiles) ? approvedFiles : []),
  );
  const deps = Object.create(null);
  const scenes = [];
  const sceneKeys = new Set();
  const errors = [];

  rows.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const filePath = approvedFilePath(row) || 'anonymous.js';
    const content =
      asTrimmedString(row.content) ||
      asTrimmedString(row.patch_content) ||
      asTrimmedString(row.source);

    if (!content) return;

    try {
      const compiled = compileApprovedFile(content, filePath, deps, Phaser);
      moduleKeysForPath(filePath).forEach((key) => {
        deps[key] = compiled;
      });
      const sceneCandidate = unwrapModuleDefault(compiled);
      if (isPhaserSceneClass(sceneCandidate, Phaser)) {
        const sceneKey =
          resolveSceneKey(/** @type {Function} */ (sceneCandidate)) ||
          filePath;
        if (sceneKeys.has(sceneKey)) {
          console.warn(
            `[PhaserGameMount] Skipping duplicate scene key "${sceneKey}" from ${filePath}`,
          );
          return;
        }
        sceneKeys.add(sceneKey);
        scenes.push(/** @type {Function} */ (sceneCandidate));
      }
    } catch (err) {
      const message = err && err.message ? String(err.message) : String(err);
      const detail = `[PhaserGameMount] Failed to compile ${filePath}: ${message}`;
      errors.push(detail);
      console.error(detail, err);
    }
  });

  return { scenes, errors, registry: deps };
}
